import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import classNames from 'classnames';
import { useToggle } from 'hooks';

const context = createContext({});
const { Provider } = context;

/**
 * Drawer component is designed to enable a clickable button containing a
 * sections title. Upon click a corresponding container of content becomes
 * visible.
 *
 * @example
 * <Drawer>
 *   <Drawer.Title>My title</Drawer.Title>
 *   <Drawer.Content>
 *     <ul>
 *       <li>Item 1</li>
 *       <li>Item 2</li>
 *       <lit>Item 3</li>
 *     </ul>
 *   </Drawer.Content>
 * </Drawer>
 *
 * @class      Drawer
 * @param      {Object}  children  The children passed into the component
 * @return     {<Drawer>}  A new Drawer component
 */
const Drawer = ({ children }) => {
  const [active, toggleActive] = useToggle(false);
  const drawerClasses = classNames('DrawerContainer', {
    'is-expanded': active,
  });

  return (
    <Provider value={{ active, toggleActive }}>
      <div className={drawerClasses}>{children}</div>
    </Provider>
  );
};

/**
 * Utilizes a string title to build a clickable button for toggling
 * the active Drawer
 *
 * @class      DrawerTitle
 * @param      {String}  children  The string title for the button
 * @return     {<Drawer.Title>}
 */
const DrawerTitle = ({ children }) => {
  const { toggleActive } = useContext(context);

  return (
    <button
      className="DrawerContainer-title d-f jc-s"
      onClick={toggleActive}
      type="button"
    >
      {children}
      <div className="DrawerContainer-arrow DrawerContainer--rightArrow" />
    </button>
  );
};

/**
 * Displays hidden content of the Drawer based on the current active state. Uses
 * max-height to animate the container into visibility.
 *
 * @class      DrawerContent
 * @param      {Object}  children  The content to be displayed when the Drawer
 *                                      is active
 * @return     {<Drawer.Content>}
 */
const DrawerContent = ({ children }) => {
  const contentRef = useRef();
  const [maxHeight, setMaxHeight] = useState();
  const { active } = useContext(context);

  useEffect(() => {
    setMaxHeight(active ? contentRef.current.scrollHeight : 0);
  }, [active]);

  return (
    <div
      className="DrawerContainer-content"
      ref={contentRef}
      style={{
        maxHeight,
      }}
    >
      {children}
    </div>
  );
};

Drawer.Title = DrawerTitle;
Drawer.Content = DrawerContent;

export default Drawer;
